import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Search, Pencil, Trash2, X, Save, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeader } from "@/context/AuthContext";
import AdminNav from "@/components/AdminNav";
import { formatIsoDate } from "@/lib/perizinan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPerizinan() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [modal, setModal] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [waNumber, setWaNumber] = useState("");
  const [waDraft, setWaDraft] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [pz, st] = await Promise.all([
        axios.get(`${API}/perizinan`, { headers: authHeader() }),
        axios.get(`${API}/settings`, { headers: authHeader() }),
      ]);
      setRows(pz.data || []);
      setWaNumber(st.data?.admin_wa_number || "");
      setWaDraft(st.data?.admin_wa_number || "");
    } catch (e) {
      if (e?.response?.status === 401) { logout(); navigate("/login", { replace: true }); }
      else toast.error("Gagal memuat data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (dateFrom && r.tanggal_pengajuan < dateFrom) return false;
      if (dateTo && r.tanggal_pengajuan > dateTo) return false;
      if (!s) return true;
      return (
        (r.nama_lengkap || "").toLowerCase().includes(s) ||
        (r.santri_username || "").toLowerCase().includes(s) ||
        (r.alasan || "").toLowerCase().includes(s)
      );
    });
  }, [rows, q, dateFrom, dateTo]);

  const openEdit = (r) => setModal({ data: { ...r } });
  const setField = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  const saveEdit = async () => {
    setBusyId("modal");
    try {
      const d = modal.data;
      const patch = {
        tanggal_pengajuan: d.tanggal_pengajuan,
        nama_lengkap: d.nama_lengkap,
        alasan: d.alasan,
        tanggal_rencana_kembali: d.tanggal_rencana_kembali,
        approver: d.approver,
        tanggal_approve: d.tanggal_approve,
      };
      await axios.patch(`${API}/perizinan/${d.id}`, patch, { headers: authHeader() });
      toast.success("Data perizinan diperbarui");
      setModal(null);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menyimpan";
      toast.error(typeof msg === "string" ? msg : "Gagal menyimpan");
    } finally { setBusyId(null); }
  };

  const doDelete = async () => {
    setBusyId(confirmDel.id);
    try {
      await axios.delete(`${API}/perizinan/${confirmDel.id}`, { headers: authHeader() });
      toast.success("Data dihapus");
      setConfirmDel(null);
      await load();
    } catch { toast.error("Gagal menghapus"); }
    finally { setBusyId(null); }
  };

  const saveWa = async () => {
    setWaBusy(true);
    try {
      await axios.put(`${API}/settings`, { admin_wa_number: waDraft }, { headers: authHeader() });
      setWaNumber(waDraft);
      toast.success("Nomor WhatsApp Admin diperbarui");
    } catch { toast.error("Gagal menyimpan nomor"); }
    finally { setWaBusy(false); }
  };

  return (
    <main className="min-h-screen bg-paper text-forest">
      <AdminNav user={user} active="perizinan" />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="text-xs tracking-[0.4em] uppercase text-gold">Dashboard Admin — Perizinan</span>
          <h1 className="mt-3 font-serif-display text-forest text-4xl md:text-5xl leading-tight">
            Data Perizinan,<br /><span className="italic text-gold">satu tempat.</span>
          </h1>
        </motion.div>

        {/* WA number setting */}
        <div className="mt-8 p-5 rounded-2xl border border-gold/30 bg-sand/60">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-2 flex items-center gap-2">
                <Phone size={12} /> Nomor WhatsApp Admin (untuk perizinan santri)
              </label>
              <input
                type="text" value={waDraft} onChange={(e) => setWaDraft(e.target.value)}
                placeholder="+6281234567890"
                className="w-full max-w-md px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
                data-testid="admin-wa-input"
              />
              <p className="mt-1 text-[10px] text-moss">
                Nomor ini otomatis dipakai saat santri submit Form Perizinan (redirect ke chat WhatsApp).
                {waNumber && ` Aktif saat ini: ${waNumber}`}
              </p>
            </div>
            <button
              onClick={saveWa} disabled={waBusy || waDraft === waNumber}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors disabled:opacity-60"
              data-testid="admin-wa-save"
            >
              {waBusy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Simpan Nomor
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-end gap-3 p-4 rounded-2xl border border-gold/30 bg-white/50">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5">Cari Nama / Alasan</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                className="pl-9 pr-4 py-2.5 bg-white/70 border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 min-w-[260px]"
                data-testid="perizinan-search" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5">Tgl Pengajuan (dari)</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 bg-white border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
              data-testid="perizinan-date-from" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5">Sampai</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 bg-white border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
              data-testid="perizinan-date-to" />
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-forest text-sm hover:bg-gold hover:text-forest transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
          {(q || dateFrom || dateTo) && (
            <button onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-moss hover:text-gold"
              data-testid="perizinan-clear-filters">
              <X size={12} /> Bersihkan filter
            </button>
          )}
          <span className="ml-auto text-[11px] text-moss">
            Menampilkan <b>{filtered.length}</b> dari {rows.length}
          </span>
        </div>

        {/* Table */}
        <div className="mt-6 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-forest text-parchment text-left">
                <tr>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Aksi</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">No</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Tgl Pengajuan</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Nama Lengkap</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Username</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Pembimbing</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Alasan</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Rencana Kembali</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Approver</th>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Tgl Approve</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="text-center py-10 text-moss">Memuat data…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-10 text-moss">Belum ada data perizinan.</td></tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={r.id} className="border-t border-gold/15 hover:bg-sand/60 transition-colors" data-testid={`perizinan-row-${r.id}`}>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(r)} className="p-2 rounded-full hover:bg-gold hover:text-forest transition-colors" aria-label="Edit" data-testid={`perizinan-edit-${r.id}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => setConfirmDel(r)} className="p-2 rounded-full hover:bg-red-600 hover:text-white transition-colors" aria-label="Hapus" data-testid={`perizinan-del-${r.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-moss">{i + 1}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{formatIsoDate(r.tanggal_pengajuan)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap font-medium">{r.nama_lengkap || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.santri_username || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.nama_pembimbing || "—"}</td>
                      <td className="px-3 py-2.5 max-w-[320px] truncate" title={r.alasan}>{r.alasan || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{formatIsoDate(r.tanggal_rencana_kembali)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.approver || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.tanggal_approve ? formatIsoDate(r.tanggal_approve) : "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {modal && (
          <EditModal
            data={modal.data} onChange={setField} onClose={() => setModal(null)}
            onSave={saveEdit} saving={busyId === "modal"}
          />
        )}
        {confirmDel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-forest/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
            <motion.div initial={{ y: 30 }} animate={{ y: 0 }} exit={{ y: 30 }}
              className="bg-parchment rounded-2xl w-full max-w-md p-6 border border-gold/40 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-serif-display text-forest text-2xl">Hapus data perizinan?</h3>
              <p className="mt-3 text-sm text-moss">Data perizinan <b>{confirmDel.nama_lengkap}</b> ({formatIsoDate(confirmDel.tanggal_pengajuan)}) akan dihapus permanen.</p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button onClick={() => setConfirmDel(null)} className="px-5 py-2 rounded-full text-forest text-sm hover:bg-sand">Batal</button>
                <button onClick={doDelete} disabled={busyId === confirmDel.id} className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-60">
                  {busyId === confirmDel.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Hapus
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function EditModal({ data, onChange, onClose, onSave, saving }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ y: 30, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-parchment rounded-2xl w-full max-w-2xl overflow-hidden border border-gold/40 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Edit Perizinan</span>
            <h3 className="font-serif-display text-forest text-2xl">{data.nama_lengkap || "—"}</h3>
          </div>
          <button onClick={onClose} className="text-forest hover:text-gold p-2"><X size={18} /></button>
        </div>

        <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Tanggal Pengajuan" type="date" value={data.tanggal_pengajuan} onChange={(v) => onChange("tanggal_pengajuan", v)} tid="edit-tanggal_pengajuan" />
          <Field label="Nama Lengkap" type="text" value={data.nama_lengkap} onChange={(v) => onChange("nama_lengkap", v)} tid="edit-nama_lengkap" />
          <div className="md:col-span-2">
            <Field label="Alasan" type="textarea" value={data.alasan} onChange={(v) => onChange("alasan", v)} tid="edit-alasan" />
          </div>
          <Field label="Tanggal Rencana Kembali" type="date" value={data.tanggal_rencana_kembali} onChange={(v) => onChange("tanggal_rencana_kembali", v)} tid="edit-tanggal_rencana_kembali" />
          <Field label="Approver" type="text" value={data.approver} onChange={(v) => onChange("approver", v)} tid="edit-approver" />
          <Field label="Tanggal Approve" type="date" value={data.tanggal_approve} onChange={(v) => onChange("tanggal_approve", v)} tid="edit-tanggal_approve" />
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gold/20">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-forest text-sm hover:bg-sand">Batal</button>
          <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, type, value, onChange, tid }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-1.5">{label}</label>
      {type === "textarea" ? (
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm" data-testid={tid} />
      ) : (
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm" data-testid={tid} />
      )}
    </div>
  );
}
